import { Component, Injector, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { DetailPageInfo } from 'src/app/core/models/detail-page-info';
import { PageInfo } from 'src/app/core/models/page-info';
import { deepCopy, genTableTranslation, getDateFormatted, isNullOrEmpty } from 'src/app/core/utils/common-functions';
import { CalendarComponent } from 'src/app/shared/components/calendar/calendar.component';
import { SortOrder } from 'src/app/core/interface/sort-order';
import { CustomsClearanceShippingReportService } from '../../services/customs-clearance-shipping-report.service';
import { finalize } from 'rxjs/operators';
import { HttpErrorResponse } from '@angular/common/http';
import { SessionBaseComponent } from 'src/app/shared/components/session.base.component';
import { DropdownOptionsService } from 'src/app/core/services/dropdown-options.service';
import { LaasOption } from 'src/app/core/models/laas-option';
import { UserPreferenceConstants } from 'src/app/core/constants/user-preference-constants.constant';
import { DataTableComponent } from 'src/app/shared/components/data-table/data-table.component';
import { PermissionConstants } from 'src/app/core/constants/permission-constants.constant';
import { CommonDataService } from 'src/app/core/services/common-data.service';
import { FileUpload } from 'primeng/fileupload';


@Component({
  selector: 'laas-customs-clearance-shipping-report',
  templateUrl: './customs-clearance-shipping-report.component.html',
  styleUrls: ['./customs-clearance-shipping-report.component.scss']
})
export class CustomsClearanceShippingReportComponent  extends SessionBaseComponent  implements OnInit {

  preferenceKey: string = UserPreferenceConstants.CustomsClearanceShippingReportHeaderTableData;
  detailPreferenceKey: string = UserPreferenceConstants.CustomsClearanceShippingReportDetailTableData;
  readonly permissionCode = PermissionConstants.ViewCustomsClearanceShippingReport;
  currentLang: string = this.laasSetting.language;

  @ViewChild('headerTable') headerTable!: DataTableComponent;
  @ViewChild('expandedTable') expandedTable!: DataTableComponent;
  @ViewChild('creationDate') createDate!: CalendarComponent;
  @ViewChild('date') date!: CalendarComponent;
  @ViewChild('fileUpload') fileUpload!: FileUpload;

  isSearchFormOpen: boolean;
  isShowEditDOModal: boolean;
  isLoading: boolean;
  columns: any[] = [];
  detailColumns: any[] = [];
  customsClearanceShippingReportData: any[];
  customsClearanceShippingReportDetailData: any[];
  showUploadDialog = false;
  isFileDialogLoading = false;
  uploadParams: any;
  file: any;

  searchForm: FormGroup;

  pageInfo: PageInfo;
  detailPageInfo: DetailPageInfo;

  sortInfo: SortOrder = {
    property: 'createDt',
    direction: -1
  };

  detailSortInfo: SortOrder = {
    property: 'createDt',
    direction: -1
  };

  customsClearanceTypeOptions: any[] = [];
  userOptions: LaasOption[] = [];

  constructor(
    injector: Injector,
    private fb: FormBuilder,
    private customsClearanceShippingReportService: CustomsClearanceShippingReportService,
    private dropdownOptionService: DropdownOptionsService,
    private commonDataService: CommonDataService) {

    super(injector);

    this.isSearchFormOpen = true;
    this.isLoading = false;
    this.isShowEditDOModal = false;

    this.columns = this.headerTableTranslation();

    this.detailColumns = this.detailTableTranslation();

    this.customsClearanceShippingReportData = [];
    this.customsClearanceShippingReportDetailData = [];

    this.searchForm = this.fb.group({
      customsDeclarationNoList: null,
      refNoList: null,
      adNoAndDoNoList: null,
      consigneeKeyList: null,
      dateStart: null,
      dateEnd: null,
      customsClearanceTypeList: null,
    });

    this.uploadParams = {
      customsDeclarationNo: null
    };

    this.pageInfo = { pageSize: 5, pageIndex: 0, totalRecords: 0, totalPages: 0 };

    this.detailPageInfo = { pageSize: 5, pageIndex: 0, totalRecords: 0, totalPages: 0 };

  }

  headerTableTranslation(): any[] {
    let fields: string[];
    let dates: string[];
    let fieldSorts: string[];
    let jsons: string[];

    fields = ['customsDeclarationNo', 'bondNo', 'consigneeKey', 'ctmCode', 'customsClearanceType', 'createUser', 'createDt', 'updateUser', 'updateDt'];
    dates = ['createDt', 'updateDt'];
    fieldSorts = ['customsDeclarationNo', 'bondNo', 'consigneeKey', 'ctmCode', 'customsClearanceType', 'createUser', 'createDt', 'updateUser', 'updateDt'];
    jsons = [
      'CustomsClearanceShippingReport.Table.HeaderColumn.CustomsDeclarationNo',
      'CustomsClearanceShippingReport.Table.HeaderColumn.BondNo',
      'CustomsClearanceShippingReport.Table.HeaderColumn.ConsigneeKey',
      'CustomsClearanceShippingReport.Table.HeaderColumn.CtmCode',
      'CustomsClearanceShippingReport.Table.HeaderColumn.CustomsClearanceType',
      'Common.Label.CreateUser',
      'Common.Label.CreateDate',
      'Common.Label.UpdateUser',
      'Common.Label.UpdateDate'
    ];

    return genTableTranslation(this.translateService, fields, dates, fieldSorts, jsons);
  }

  detailTableTranslation(): any[] {
    let fields: string[];
    let dates: string[];
    let fieldSorts: string[];
    let jsons: string[];

    fields = ['declarationItemNo', 'date', 'partNumber', 'externalPartNumber', 'productName', 'qty', 'unit', 'nw', 'gw', 'wgtUnit', 'unitPrice', 'currency', 'dutiablePrice', 'origDeclNo', 'origDeclItemNo', 'remarks', 'commodityClassificationNo', 'refNo', 'refItemNo', 'inOutDocNo', 'origDeclStorDt', 'vendorOrder', 'coo', 'price', 'ctmCode', 'tradPartyCode', 'po', 'poItemNo', 'itemCount', 'itemCountUnit', 'statsUnit', 'rptTransVchr', 'createUser', 'createDt', 'updateUser', 'updateDt'];
    dates = ['date', 'createDt', 'updateDt'];
    fieldSorts = ['declarationItemNo', 'date', 'partNumber', 'externalPartNumber', 'productName', 'qty', 'unit', 'nw', 'gw', 'wgtUnit', 'unitPrice', 'currency', 'dutiablePrice', 'origDeclNo', 'origDeclItemNo', 'remarks', 'commodityClassificationNo', 'refNo', 'refItemNo', 'inOutDocNo', 'origDeclStorDt', 'vendorOrder', 'coo', 'price', 'ctmCode', 'tradPartyCode', 'po', 'poItemNo', 'itemCount', 'itemCountUnit', 'statsUnit', 'rptTransVchr', 'createUser', 'createDt', 'updateUser', 'updateDt'];
    jsons = [
      'CustomsClearanceShippingReport.Table.DetailColumn.DeclarationItemNo',
      'CustomsClearanceShippingReport.Table.DetailColumn.Date',
      'CustomsClearanceShippingReport.Table.DetailColumn.PartNumber',
      'CustomsClearanceShippingReport.Table.DetailColumn.ExternalPartNumber',
      'CustomsClearanceShippingReport.Table.DetailColumn.ProductName',
      'CustomsClearanceShippingReport.Table.DetailColumn.Qty',
      'CustomsClearanceShippingReport.Table.DetailColumn.Unit',
      'CustomsClearanceShippingReport.Table.DetailColumn.Nw',
      'CustomsClearanceShippingReport.Table.DetailColumn.Gw',
      'CustomsClearanceShippingReport.Table.DetailColumn.WgtUnit',
      'CustomsClearanceShippingReport.Table.DetailColumn.UnitPrice',
      'CustomsClearanceShippingReport.Table.DetailColumn.Currency',
      'CustomsClearanceShippingReport.Table.DetailColumn.DutiablePrice',
      'CustomsClearanceShippingReport.Table.DetailColumn.OrigDeclNo',
      'CustomsClearanceShippingReport.Table.DetailColumn.OrigDeclItemNo',
      'CustomsClearanceShippingReport.Table.DetailColumn.Remarks',
      'CustomsClearanceShippingReport.Table.DetailColumn.CommodityClassificationNo',
      'CustomsClearanceShippingReport.Table.DetailColumn.RefNo',
      'CustomsClearanceShippingReport.Table.DetailColumn.RefItemNo',
      'CustomsClearanceShippingReport.Table.DetailColumn.InOutDocNo',
      'CustomsClearanceShippingReport.Table.DetailColumn.OrigDeclStorDt',
      'CustomsClearanceShippingReport.Table.DetailColumn.VendorOrder',
      'CustomsClearanceShippingReport.Table.DetailColumn.Coo',
      'CustomsClearanceShippingReport.Table.DetailColumn.Price',
      'CustomsClearanceShippingReport.Table.DetailColumn.CtmCode',
      'CustomsClearanceShippingReport.Table.DetailColumn.TradPartyCode',
      'CustomsClearanceShippingReport.Table.DetailColumn.PO',
      'CustomsClearanceShippingReport.Table.DetailColumn.PoItemNo',
      'CustomsClearanceShippingReport.Table.DetailColumn.ItemCount',
      'CustomsClearanceShippingReport.Table.DetailColumn.ItemCountUnit',
      'CustomsClearanceShippingReport.Table.DetailColumn.StatsUnit',
      'CustomsClearanceShippingReport.Table.DetailColumn.RptTransVchr',
      'Common.Label.CreateUser',
      'Common.Label.CreateDate',
      'Common.Label.UpdateUser',
      'Common.Label.UpdateDate',
    ];

    return genTableTranslation(this.translateService, fields, dates, fieldSorts, jsons);
  }

  async ngOnInit(): Promise<void> {
    await super.ngOnInit();

    this.commonDataService.getCodeConverter(this.laasSetting.language, 'DropdownList', 'CustomsClearanceType').subscribe({
      next: result => {
        this.customsClearanceTypeOptions = [...result].map((x: any) => {
          return {
            label: `${x.codeKey} - ${x.codeDesc}`,
            value: x.codeKey,
          };
        });
      }
    });

    this.dropdownOptionService.getUserOptions().subscribe({
      next: (options: any[]) => {
        this.userOptions = options.map(x => ({ label: x.userName, value: x.uuid}));
      }
    });

  }

  chipsAdd(event: any, fromControlName: any): void {
    const value: string = event.value;
    const parsedValues: string[] = value.split('\r\n').filter(x => !isNullOrEmpty(x) && x !== '\r\n');
    const formControl = this.searchForm.get(fromControlName);
    formControl?.value.splice(formControl?.value.indexOf(value));
    formControl?.setValue(formControl?.value.concat(parsedValues));
  }

  optionsDataOnChange(data: any, type: string): void {
    switch (type) {
      case 'customer':
        this.searchForm.controls.consigneeKeyList.setValue(data);
        break;
    }
  }

  searchBtnClick(): void {
    this.pageInfo.pageIndex = 0;
    this.headerTable.collapseAllRow();
    this.searchCustomsClearanceShippingReportHeader();
  }

  resetBtnClick(): void {
    this.createDate.ngOnInit();
    this.date.ngOnInit();
    this.searchForm.reset();
  }

  searchDetailClick(event: any): void {
    const rowData = event.rowData;
    const isClosed = event.expanded;

    if (isClosed) {
      return;
    }

    this.headerTable.selectedRow = rowData;
    this.searchCustomsClearanceShippingReportDetail();

  }

  tablePageChange(event: any): void {
    this.pageInfo.pageSize = event.rows;
    this.pageInfo.pageIndex = event.first / this.pageInfo.pageSize;
    this.searchCustomsClearanceShippingReportHeader();
  }

  detailTablePageChange(event: any, ): void {
    this.detailPageInfo.pageSize = event.rows;
    this.detailPageInfo.pageIndex = event.first / this.detailPageInfo.pageSize;
    this.searchCustomsClearanceShippingReportDetail();
  }

  tableSort(event: any): void {
    this.sortInfo = { property: event.field, direction: event.order };
    this.searchCustomsClearanceShippingReportHeader();
  }

  detailTableSort(event: any): void {
    this.detailSortInfo = { property: event.field, direction: event.order };
    this.searchCustomsClearanceShippingReportDetail();
  }


  private searchCustomsClearanceShippingReportHeader(): void {

    const params = deepCopy(this.searchForm.value);

    params.pageIndex = this.pageInfo.pageIndex;
    params.pageSize = this.pageInfo.pageSize;
    params.sortOrder = this.sortInfo;

    this.isLoading = true;
    this.customsClearanceShippingReportService.queryCustomsClearanceShippingReportHeader(params)
      .pipe(finalize(() => this.isLoading = false))
      .subscribe({
        next: result => {
          this.isSearchFormOpen = false;
          this.customsClearanceShippingReportData = result.content;
          this.pageInfo.totalRecords = result.totalElements;
       },
        error: (err: HttpErrorResponse) => {
          this.toastErrorMessage(err.error);
        }
    });

  }

  private searchCustomsClearanceShippingReportDetail(): void {

    this.detailPageInfo.pageIndex = 0;

    if (!this.headerTable.selectedRow) {
      return;
    }

    const queryLeaveWhDetailPortalRequest = {
      customsClearanceShippingReportSeqNo: !this.headerTable.selectedRow ? null : this.headerTable.selectedRow.seqNo,
      pageIndex: this.detailPageInfo.pageIndex,
      pageSize: this.detailPageInfo.pageSize,
      sortOrder: this.detailSortInfo
    };

    this.isLoading = true;
    this.customsClearanceShippingReportService.queryCustomsClearanceShippingReportDetail(queryLeaveWhDetailPortalRequest)
      .pipe(finalize(() => this.isLoading = false))
      .subscribe({
        next: result => {
          this.isSearchFormOpen = false;
          this.customsClearanceShippingReportDetailData = result.content;
          this.detailPageInfo.totalRecords = result.totalElements;

       },
        error: (err: HttpErrorResponse) => {
          this.toastErrorMessage(err.error);
        }
    });

  }



  dateOnChange(date: any, type: string): void {
    switch (type) {
      case 'creationDate' :
        this.searchForm.controls.createDtStart.setValue(date.startDate);
        this.searchForm.controls.createDtEnd.setValue(date.endDate);
        break;
      case 'strDate' :
        this.searchForm.controls.dateStart.setValue(date.startDate);
        this.searchForm.controls.dateEnd.setValue(date.endDate);
        break;
    }
  }

  downloadFile(downloadType: string): void {

    this.isLoading = true;

    const customsDeclarationNo = this.headerTable.selectedRow.customsDeclarationNo;
    if (isNullOrEmpty(customsDeclarationNo)){
      this.toastService.warn(this.translateService.instant('CustomsClearanceShippingReport.Message.MissCustomsDeclarationNo'));
      this.isLoading = false;
      return;
    }

    this.customsClearanceShippingReportService.downloadCustomsClearanceShippingReport(downloadType, customsDeclarationNo).subscribe({
      next: x => {
        const byteStr = atob(x.file);
        const arrayBuffer: ArrayBuffer = new ArrayBuffer(byteStr.length);
        const int8Array: Uint8Array = new Uint8Array(arrayBuffer);
        for (let i = 0; i < byteStr.length; i++) {
          int8Array[i] = byteStr.charCodeAt(i);
        }

        const data = new Blob(
          [int8Array], { type: downloadType === 'PDF' ? 'application/pdf' : 'application/vnd.ms-excel' }
        );

        const url = URL.createObjectURL(data);
        const linkEle = document.createElement('a');
        linkEle.href = url;
        linkEle.download = x.fileName;
        linkEle.click();
        URL.revokeObjectURL(url);
        linkEle.remove();

        this.isLoading = false;
      },
      error: err => {
        this.toastErrorMessage(err.error);
        this.isLoading = false;
      },
      complete: () => {
        this.isLoading = false;
      }
    });
  }

  downloadCustomsTrade() {
    this.isLoading = true;

    const customsDeclarationNo = this.headerTable.selectedRow.customsDeclarationNo;
    if (isNullOrEmpty(customsDeclarationNo)){
      this.toastService.warn(this.translateService.instant('CustomsClearanceShippingReport.Message.MissCustomsDeclarationNo'));
      this.isLoading = false;
      return;
    }

    this.customsClearanceShippingReportService.downloadCustomsTradeExcel(customsDeclarationNo).subscribe({
      next: x => {
        const byteStr = atob(x.file);
        const arrayBuffer: ArrayBuffer = new ArrayBuffer(byteStr.length);
        const int8Array: Uint8Array = new Uint8Array(arrayBuffer);
        for (let i = 0; i < byteStr.length; i++) {
          int8Array[i] = byteStr.charCodeAt(i);
        }

        const data = new Blob(
          [int8Array], { type: 'application/vnd.ms-excel' }
        );

        const url = URL.createObjectURL(data);
        const linkEle = document.createElement('a');
        linkEle.href = url;
        linkEle.download = x.fileName;
        linkEle.click();
        URL.revokeObjectURL(url);
        linkEle.remove();

        this.isLoading = false;
      },
      error: err => {
        this.toastErrorMessage(err.error);
        this.isLoading = false;
      },
      complete: () => {
        this.isLoading = false;
      }
    });
  }

  uploadFile(): void {
    this.showUploadDialog = true;
  }

  onUploadDialogHide(): void {
    this.uploadParams.customsDeclarationNo = null;
    if (this.fileUpload) {
      this.fileUpload.clear();
    }
  }

  fileUploader(event: any): void{
    this.saveReportBtn(event);
  }

  clearFile(): void{
    this.file = null;
  }

  async saveReportBtn(event: any): Promise<void> {

    this.file = event.files[0];

    const customsDeclarationNo = this.uploadParams.customsDeclarationNo;
    if (isNullOrEmpty(customsDeclarationNo)) {
      this.toastService.warn(this.translateService.instant('CustomsClearanceShippingReport.Dialog.Message.CheckRequired'));
      return;
    }
    if (isNullOrEmpty(this.file)) {
      this.toastService.warn(this.translateService.instant('CustomsClearanceShippingReport.Dialog.Message.CheckFile'));
      return;
    }

    const customsClearanceShippingReport = await this.customsClearanceShippingReportService.queryCustomsDeclarationNoIsExists(customsDeclarationNo);

    if (customsClearanceShippingReport === null) {
      this.uploadFileAndUpdateCustomsDeclarationNo(customsDeclarationNo);
      return;
    }

    this.confirmationService.confirm({
      header: this.translateService.instant('CustomsClearanceShippingReport.Dialog.Message.Header'),
      message: this.translateService.instant('CustomsClearanceShippingReport.Dialog.Message.CheckCover'),
      icon: 'pi pi-info-circle',
      accept: () => {
        this.uploadFileAndUpdateCustomsDeclarationNo(customsDeclarationNo);
      }
    });
  }

  uploadFileAndUpdateCustomsDeclarationNo(customsDeclarationNo: string): void{
    this.isFileDialogLoading = true;
    const formData = new FormData();
    formData.append('customsDeclarationNo', customsDeclarationNo);
    formData.append('file', this.file);

    this.customsClearanceShippingReportService.uploadFileAndUpdateCustomsDeclarationNo(formData).subscribe({
      next: rspStatus => {
        if (rspStatus) {
          this.toastService.success(this.translateService.instant('CustomsClearanceShippingReport.Dialog.Message.UploadSuccess'));
        } else {
          this.toastService.error(this.translateService.instant('CustomsClearanceShippingReport.Dialog.Message.UploadFailed'));
        }
      },
      error: err => {
        this.toastErrorMessage(err.error);
      },
      complete: () => {
        this.uploadParams = {};
        this.isFileDialogLoading = false;
        this.showUploadDialog = false;
      }
    });
  }
}
